import { useCallback, useEffect, useState } from 'react';
import jwt from 'jwt-decode';

import { Auth, smt, useRequest } from '.';

type JWTData = {
  id: string;
  validation?: 'phone';
};

type State = Omit<Auth, 'setToken'>;

const useAuth = (): Auth => {
  const [state, setState] = useState<Omit<Auth, 'setToken'>>(() => {
    const token = localStorage.getItem('token');
    return { ...setupAuth(token), status: token ? 'refreshing' : 'loggedOut' };
  });

  const request = useRequest({ autoclear: true });
  const setToken = useCallback((token: string | null) => setState(setupAuth(token)), []);

  useEffect(() => {
    switch (state.status) {
      case 'loggedOut':
        setState({ status: 'creating' });

        request<string>({
          url: '/userInteraction/anonymous',
          method: 'POST',
          data: { origin: 'plays' },
          onSuccess: setToken,
          onError: () => setState({ status: 'error' }),
        });

        break;

      case 'refreshing':
        request<string>({
          url: '/userInteraction/refreshToken',
          method: 'POST',
          onSuccess: setToken,
          onError: error => {
            if (error.response?.status === 401) {
              setState(setupAuth(null));
            } else {
              setState({ status: 'error' });
            }
          },
        });

        break;

      default:
        break;
    }
  }, [request, setToken, state.status]);

  return { ...state, setToken };
};

export default useAuth;

const setupAuth = (token?: string | null): State => {
  if (token) {
    localStorage.setItem('token', token);
    smt.defaults.headers.common.Authorization = token;

    const { id: userId, validation } = jwt<JWTData>(token);
    return { status: 'loggedIn', token, userId, validation };
  }

  localStorage.removeItem('token');
  delete smt.defaults.headers.common.Authorization;

  return { status: 'loggedOut' };
};
